/**
 * @module Tab
 * @description
 */
import React, { FC, useContext, useMemo } from "react";
import DynamicButton from "ui/components/Buttons/DynamicButton";
import styles from "./styles/Tab.module.css";
import TrackElementViewed from "~/components/helpers/TrackElementViewed";
import { MonarchSlot } from '@redventures/cohesion-utils-react';
import { PricingContext } from "ui/providers/pricing";
import { Pricing } from "ui/types/common";

interface Product {
  name: string;
  heading: string;
  legal: string;
  valueProps: {
    heading: string;
    items: ( string|JSX.Element )[];
  }
}

interface Props {
  product: Product;
  index: number;
  activeTab: number;
  position: string;
}

const Tab: FC<Props> = ({ ...props }) => {
  const { product, index, activeTab, position } = props;
  const { valueProps } = product;
  const { pricing } = useContext(PricingContext) as unknown as { pricing: Pricing };

  const productPricing = useMemo(() => pricing[product.name], [pricing, product.name]);
  const price = productPricing?.price;
  const strikethroughPrice = productPricing?.strikethroughPrice;

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${index}`}
      aria-labelledby={`tabpanel-${index}`}
      tabIndex={0}
      className={styles.tabPanel}
      data-visible={activeTab === index}
    >
      <div className={styles.product}>
        <div className={styles.info}>
          <TrackElementViewed position={`${position} - TAB ${index + 1}`}>
            <p className={styles.heading}>{product.heading}</p>
          </TrackElementViewed>

          <div className={styles.price}>
            <span className={styles.promo}>${price}</span>
            <span className={styles.promo__asterisk}>*</span>
            {strikethroughPrice && (
              <span className={styles.base}>${strikethroughPrice}</span>
            )}
          </div>
          <p className={styles.legal}>{product.legal}</p>
        </div>
      </div>
      {/* <-- .product --> */}

      <div className={styles.content}>
        <h2 className={styles.heading}>{valueProps.heading}</h2>

        <ul className={styles.ul}>
          {valueProps.items.map((item: string | JSX.Element, index: number) => (
            <li key={index} className={styles.li}>
              {item}
            </li>
          ))}
        </ul>

        {/* Product Tabset | Main CTA */}
        <MonarchSlot slotId="smbLpProductTabsetMainCta">
          <DynamicButton type="cart" position={ position } />
        </MonarchSlot>
      </div>{ /* <-- .content --> */ }
    </div>
  );
};

Tab.displayName = "Tab";

export default Tab;
